import cn from 'classnames'
import { DataModel } from 'legacy/lut/classes/models/DataModel'
import { useGetPartnerData } from 'legacy/lut/features/partner/hooks'
import { updatePartnerData } from 'legacy/lut/features/partner/partnerSlice'
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { ActiveFiltersMap } from 'clickNgo/components/dataFilterBar'
import { RequestStatus } from 'clickNgo/configs/service/common'
import { ContactEditor } from 'clickNgo/containers/contactEditor'
import {
  ContactState,
  ContactStatus,
  getContactStateKey,
  IContact,
} from 'clickNgo/entities/contact'
import {
  exportContacts,
  useGetContactsWithFlagsByPartnerQuery,
} from 'clickNgo/entities/contact/api'
import { emptyContactFlags, IContactFlags } from 'clickNgo/entities/contactFlag'
import { fetchPartnerMissedContacts } from 'clickNgo/entities/partner/api'
import { IPartner } from 'clickNgo/entities/partner/models'
import { ContactCreator } from 'clickNgo/features/contact/contactCreator'
import { useCheckReadOnlyAccess } from 'clickNgo/hooks/access'
import { partnerContactsTranslation } from 'clickNgo/widgets/partnerContacts/translation'
import { InfoBlock, InfoBlockType } from 'cng-common/components/infoBlock'
import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { SimpleTable, usePagination } from 'cng-common/components/simpleTable'
import { Translation } from 'cng-common/features/translations'
import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'
import { Nullable } from 'cng-common/models/utils'
import { getUrlParam } from 'core-components/helpers/url'
import { Button } from 'core-components/library/button'
import { Checkbox } from 'core-components/library/checkbox'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import { contactApi } from 'onboarding/features/api/contactApi'
import { CustomTableHeaderCell } from 'shared/components/customTableHeaderCell'
import { sort } from 'shared/components/customTableHeaderCell/helpers'
import { SortCriteria } from 'shared/components/customTableHeaderCell/models'

import { ContactControls } from './components/contactControls'
import { ContactDetails } from './components/contactDetails'
import { ContactTypeSelector, ContactTypeSelectorItem } from './components/contactTypeSelector'
import { DraftContactDialog } from './components/draftContactDialog/DraftContactDialog'
import { FilterPanel } from './components/filterPanel'
import { PartnerSelector } from './components/partnerSelector'
import { contactTableColumns, ContactTypeSelectorSegment } from './configs'
import {
  filterByIndividualFilters,
  filterByListType,
  filterBySearchQuery,
  sortContacts,
} from './helpers'

import styles from './PartnerContacts.module.scss'

interface IProps {
  partnerId: string
  source?: string
  onboardingRequestId?: number
  config?: PartnerContactsConfig
  onContactSelect?: (contact: IContact) => void
}

export interface PartnerContactsConfig {
  isOnboarding?: boolean
  isPartnerSelectorVisible?: boolean
  isDraftContactCreationEnabled?: boolean
  defaultSegment?: ContactTypeSelectorSegment
  segments?: Array<ContactTypeSelectorItem>
}

const DEFAULT_CONFIG: Required<PartnerContactsConfig> = {
  isOnboarding: false,
  isPartnerSelectorVisible: true,
  isDraftContactCreationEnabled: false,
  defaultSegment: ContactTypeSelectorSegment.SelectedPartner,
  segments: [
    {
      key: ContactTypeSelectorSegment.SelectedPartner,
      text: partnerContactsTranslation.contacts.controls.segment.partner,
    },
    {
      key: ContactTypeSelectorSegment.AllContacts,
      text: partnerContactsTranslation.contacts.controls.segment.all,
    },
    {
      key: ContactTypeSelectorSegment.MyRequest,
      text: partnerContactsTranslation.contacts.controls.segment.my,
    },
    {
      key: ContactTypeSelectorSegment.NewContacts,
      text: partnerContactsTranslation.contacts.controls.segment.new,
    },
  ],
}

export const PartnerContacts = memo<IProps>(
  ({ partnerId, onboardingRequestId, source, config, onContactSelect }) => {
    const getContactKey = useCallback((contact: Pick<IContact, 'contactId' | 'partnerId'>) => {
      return getContactStateKey(contact)
    }, [])

    const resolvedConfig = useMemo(
      () => ({
        ...DEFAULT_CONFIG,
        ...config,
        segments: config?.segments ?? DEFAULT_CONFIG.segments,
      }),
      [config]
    )

    // ----------------------------- VARIABLES ---------------------------------
    const user = useSelector(selectUser)
    const dispatch = useAppDispatch()
    const resolvedOnboardingRequestId =
      onboardingRequestId ?? (Number(getUrlParam('rid')) || undefined)
    const { partnerData: currentPartner, status: currentPartnerStatus } =
      useGetPartnerData(partnerId)

    const {
      data: contactState = {
        allContactIds: [],
        contactFlagsById: {},
        contactsById: {},
      } as ContactState,
      isLoading: isLoadingContacts,
      isFetching: isFetchingContacts,
      refetch: refetchContacts,
    } = useGetContactsWithFlagsByPartnerQuery(
      {
        isOnboarding: resolvedConfig.isOnboarding,
        topPartnerId: currentPartner?.topPartnerId,
        partnerId: currentPartner?.partnerId,
      },
      {
        skip:
          !currentPartner?.topPartnerId &&
          !(resolvedConfig.isOnboarding && Boolean(currentPartner?.partnerId)),
      }
    )

    const allContactFlagsById = useMemo<Record<string, IContactFlags>>(() => {
      return contactState?.contactFlagsById || {}
    }, [contactState.contactFlagsById])

    const getContactFlags = useCallback(
      (contact: Pick<IContact, 'contactId' | 'partnerId'>) => {
        return (
          allContactFlagsById[getContactKey(contact)] ??
          allContactFlagsById[contact.contactId] ??
          emptyContactFlags
        )
      },
      [allContactFlagsById, getContactKey]
    )

    const allContactIds = useMemo<string[]>(
      () => contactState.allContactIds || [],
      [contactState.allContactIds]
    )

    const [initialValues, setInitialValues] = useState<Partial<IContact> | undefined>()
    const contactsById = contactState.contactsById
    const allContacts = useMemo<IContact[]>(() => {
      return allContactIds.map((id) => contactsById[id])
    }, [contactsById, allContactIds])

    const [searchQuery, setSearchQuery] = useState('')

    const [selectedFilter, setSelectedFilter] = useState<ContactTypeSelectorSegment>(
      resolvedConfig.defaultSegment
    )

    const [individualFilters, setIndividualFilters] = useState<ActiveFiltersMap>({})

    const [selectedContacts, setSelectedContacts] = useState<Array<IContact>>([])
    const [expandedContacts, setExpandedContacts] = useState<Array<IContact>>([])
    const [selectedPartner, setSelectedPartner] = useState<IPartner | null>(null)
    const [currentEditableContact, setCurrentEditableContact] = useState<IContact | null>(null)
    const [currentEditableContactFlags, setCurrentEditableContactFlags] =
      useState<IContactFlags>(emptyContactFlags)

    const [sortOrder, setSortOrder] = useState<SortCriteria<IContact>>({})

    const [isNewContactDialogVisible, setIsNewContactDialogVisible] = useState(false)
    const [isDraftContactDialogVisible, setIsDraftContactDialogVisible] = useState(false)
    const [hasShadow, setHasShadow] = useState(false)
    const [isLoading, setIsLoading] = useState(false)

    const isReadOnlyRoleAccess = useCheckReadOnlyAccess()

    // Pre-compute contacts per segment so ContactTypeSelector can show counts
    const contactsBySegment = useMemo(() => {
      return Object.fromEntries(
        resolvedConfig.segments.map(({ key }) => [
          key,
          filterByListType(allContacts, key, {
            modifiedBy: user.email,
            partnerId: selectedPartner?.partnerId || partnerId,
          }),
        ])
      ) as Partial<Record<ContactTypeSelectorSegment, IContact[]>>
    }, [resolvedConfig.segments, allContacts, user.email, selectedPartner?.partnerId, partnerId])

    const filteredContacts = useMemo(() => {
      const filteredByListType = contactsBySegment[selectedFilter] ?? []
      const filteredBySearchQuery = filterBySearchQuery(filteredByListType, searchQuery)
      const filteredByIndividualFilters = filterByIndividualFilters(
        filteredBySearchQuery,
        allContactFlagsById,
        individualFilters
      )

      return sort([...filteredByIndividualFilters].sort(sortContacts), sortOrder)
    }, [
      contactsBySegment,
      selectedFilter,
      searchQuery,
      allContactFlagsById,
      individualFilters,
      sortOrder,
    ])

    const tableRef = useRef<HTMLTableElement>(null)
    const { current: pageSize } = useRef(10)
    const { onPageChange, page, paginatedList, total } = usePagination(filteredContacts, pageSize)
    const tableColumnsCount = contactTableColumns.length + 2 + Number(Boolean(onContactSelect))

    const isSelectedAllContacts = useMemo<boolean>(
      () => Boolean(selectedContacts.length && selectedContacts.length === filteredContacts.length),
      [selectedContacts, filteredContacts]
    )
    const isSelectedSomeContacts = useMemo(
      () => selectedContacts.length > 0 && selectedContacts.length < filteredContacts.length,
      [selectedContacts, filteredContacts]
    )

    const isContactEditorVisible = useMemo(
      () => Boolean(currentEditableContact && currentPartner && currentEditableContactFlags),
      [currentEditableContact, currentPartner, currentEditableContactFlags]
    )

    const currentEditableContactWithOnboardingRequestId = useMemo<Nullable<IContact>>(() => {
      if (!currentEditableContact) {
        return null
      }

      return {
        ...currentEditableContact,
        onboardingRequestId: currentEditableContact.onboardingRequestId ?? onboardingRequestId,
      }
    }, [currentEditableContact, onboardingRequestId])

    const hasMissedRoles = useMemo(() => {
      return Boolean(currentPartner && currentPartner.missedRoles.length > 0)
    }, [currentPartner])

    const missedRolesInfo = useMemo(() => {
      const missedRoles = currentPartner?.missedRoles || []

      const replaceParams = [
        {
          key: '{roles}',
          value: missedRoles.join(', ') || '',
        },
      ]

      return (
        <Translation
          text={partnerContactsTranslation.contacts.warnings.missedRoles}
          span
          replaceParams={replaceParams}
        />
      )
    }, [currentPartner?.missedRoles])
    // --------------------------- FETCH DATA --------------------------------

    const updateContactList = useCallback(async () => {
      if (
        currentPartner?.topPartnerId ||
        (currentPartner?.partnerId && resolvedConfig.isOnboarding)
      ) {
        await refetchContacts()
      }
    }, [
      currentPartner?.topPartnerId,
      currentPartner?.partnerId,
      refetchContacts,
      resolvedConfig.isOnboarding,
    ])

    const updateContactsWithMultiplePartners = useCallback(async () => {
      if (resolvedOnboardingRequestId) {
        await dispatch(
          contactApi.getContactsWithMultiplePartners({ requestId: resolvedOnboardingRequestId })
        )
      }
    }, [dispatch, resolvedOnboardingRequestId])

    // ----------------------------- HANDLERS ---------------------------------

    const resetSettings = useCallback(() => {
      setSelectedPartner(null)
      setSearchQuery('')
      setSelectedFilter(resolvedConfig.defaultSegment)
    }, [resolvedConfig.defaultSegment])

    const updatePartnerMissedContacts = useCallback(async () => {
      return fetchPartnerMissedContacts(currentPartner!.partnerId, { isForce: true })
        .then((result) => {
          const missedRoles = result.missedRoles || []
          dispatch(
            updatePartnerData({
              data: { ...currentPartner!, missedRoles },
              status: RequestStatus.FULFILLED,
              message: '',
            })
          )
        })
        .finally(() => {
          setIsLoading(false)
        })
    }, [currentPartner, dispatch])

    const handleOnSelectPartner = useCallback((partner: IPartner) => {
      setSelectedPartner(partner)
    }, [])

    const handleScroll = () => {
      setHasShadow(Boolean(tableRef?.current?.scrollLeft))
    }

    const handleOnSegmentSelect = useCallback(
      (index: number) => {
        setSelectedFilter(index)
        onPageChange(0)
      },
      [onPageChange]
    )

    const handleOnMassContactSelect = useCallback(() => {
      setSelectedContacts((prev) => {
        if (prev.length === filteredContacts.length) {
          return []
        }
        return [...filteredContacts]
      })
    }, [filteredContacts])

    const handleOnContactSelect = useCallback(
      (contact: IContact) => {
        setSelectedContacts((prev) => {
          if (prev.includes(contact)) {
            return prev.filter((item) => getContactKey(item) !== getContactKey(contact))
          }
          return [...prev, contact]
        })
      },
      [getContactKey]
    )

    const handleOnRowClick = useCallback(
      (contact: IContact) => {
        setExpandedContacts((prev) => {
          if (prev.includes(contact)) {
            return prev.filter((item) => getContactKey(item) !== getContactKey(contact))
          }
          return [...prev, contact]
        })
      },
      [getContactKey]
    )

    const handleOnCreateDraftContact = useCallback(() => {
      if (currentPartner) {
        setInitialValues(undefined)
        setIsDraftContactDialogVisible(true)
      }
    }, [currentPartner])

    const handleOnCreateContact = useCallback(() => {
      if (currentPartner) {
        setInitialValues(undefined)
        setIsNewContactDialogVisible(true)
      }
    }, [currentPartner])

    // Chooses between draft flow and regular creation flow based on config
    const createContactHandler = useMemo(
      () =>
        resolvedConfig.isDraftContactCreationEnabled
          ? handleOnCreateDraftContact
          : handleOnCreateContact,
      [
        resolvedConfig.isDraftContactCreationEnabled,
        handleOnCreateDraftContact,
        handleOnCreateContact,
      ]
    )

    const handleOnContinueContactCreation = useCallback(
      (data: DataModel | null) => {
        if (currentPartner && data) {
          setInitialValues({
            firstName: data.get('FirstName'),
            lastName: data.get('LastName'),
            email: data.get('Email'),
            country: data.get('Country'),
            preferredLanguage: data.get('PreferredLanguage'),
            onboardingRequestId,
            source,
          })
          setIsDraftContactDialogVisible(false)
          setIsNewContactDialogVisible(true)
        }
      },
      [currentPartner, onboardingRequestId, source]
    )

    const onCreateAsDraft = useCallback(async () => {
      setIsLoading(true)
      setIsDraftContactDialogVisible(false)
      try {
        await updateContactList()
        await updateContactsWithMultiplePartners()
      } finally {
        setIsLoading(false)
      }
    }, [updateContactList, updateContactsWithMultiplePartners])

    const onContactCreatorCloseHandler = useCallback(() => {
      setIsNewContactDialogVisible(false)
      setInitialValues(undefined)
    }, [])

    const onContactCreatorApplyHandler = useCallback(
      async (contact: IContact) => {
        setIsNewContactDialogVisible(false)
        setIsLoading(true)

        if (contact.hpRoles.length) {
          await updatePartnerMissedContacts()
        }
        await updateContactList()
        await updateContactsWithMultiplePartners()
        setIsLoading(false)
      },
      [updateContactList, updateContactsWithMultiplePartners, updatePartnerMissedContacts]
    )

    const onDraftContactCreatorCloseHandler = useCallback(() => {
      setIsDraftContactDialogVisible(false)
    }, [])

    const onReplacementContactCreateHandler = useCallback(
      async (contact: IContact) => {
        setIsLoading(true)
        try {
          if (contact.hpRoles.length) {
            await updatePartnerMissedContacts()
          }
          await updateContactList()
          await updateContactsWithMultiplePartners()
        } finally {
          setIsLoading(false)
        }
      },
      [updateContactList, updateContactsWithMultiplePartners, updatePartnerMissedContacts]
    )

    const onContactEditHandler = useCallback(
      (contact: IContact, event?: MouseEvent) => {
        event && event.stopPropagation()
        setCurrentEditableContact(contact)
        setCurrentEditableContactFlags(getContactFlags(contact))
      },
      [getContactFlags]
    )

    const onSelectedContactsEditHandler = useCallback(() => {
      if (selectedContacts.length === 1) {
        onContactEditHandler(selectedContacts[0])
      }
    }, [onContactEditHandler, selectedContacts])

    const onContactSelectHandler = useCallback(
      (contact: IContact, event: MouseEvent) => {
        event.stopPropagation()
        onContactSelect?.(contact)
      },
      [onContactSelect]
    )

    const onContactsExportHandler = useCallback(async () => {
      if (selectedPartner?.partnerId) {
        setIsLoading(true)
        if (selectedFilter === ContactTypeSelectorSegment.AllContacts) {
          await exportContacts({
            topPartnerId: selectedPartner!.partnerId,
            searchQuery,
            individualFilters,
          })
        } else {
          await exportContacts({
            partnerId: selectedPartner!.partnerId,
            searchQuery,
            individualFilters,
          })
        }
        setIsLoading(false)
      }
    }, [selectedPartner, selectedFilter, searchQuery, individualFilters])

    const onContactsCollapseHandler = useCallback(() => {
      setExpandedContacts([])
    }, [])

    const onContactEditorCloseHandler = useCallback(() => {
      setCurrentEditableContact(null)
      setCurrentEditableContactFlags(emptyContactFlags)
    }, [])

    const onContactUpdateHandler = useCallback(
      async (contact: Nullable<IContact>) => {
        if (contact) {
          if (contact.modifiedValues.includes('hpRoles')) {
            await updatePartnerMissedContacts()
          }
        } else {
          setCurrentEditableContact(null)
        }

        const { data } = await refetchContacts()

        if (contact) {
          await updateContactsWithMultiplePartners()
        }

        if (data && contact) {
          setCurrentEditableContact(data.contactsById[getContactKey(contact)])
        }
      },
      [
        getContactKey,
        refetchContacts,
        updateContactsWithMultiplePartners,
        updatePartnerMissedContacts,
      ]
    )

    const onContactFlagsUpdateHandler = useCallback(async () => {
      const { data } = await refetchContacts()
      if (currentEditableContact && data && data.contactFlagsById) {
        setCurrentEditableContactFlags(
          data.contactFlagsById[getContactKey(currentEditableContact)] ??
            data.contactFlagsById[currentEditableContact.contactId] ??
            emptyContactFlags
        )
      }
    }, [currentEditableContact, getContactKey, refetchContacts])

    const onSortOrderChangeHandler = useCallback(
      (field: keyof IContact, value: '' | 'asc' | 'desc') => {
        setSortOrder({ [field]: value })
      },
      []
    )

    // ----------------------------- EFFECTS ---------------------------------

    useEffect(() => {
      setSelectedFilter(resolvedConfig.defaultSegment)
    }, [resolvedConfig.defaultSegment, partnerId])

    useEffect(() => {
      const isLoadingState =
        isLoadingContacts || isFetchingContacts || currentPartnerStatus === RequestStatus.PENDING
      setIsLoading(isLoadingState)
    }, [isLoadingContacts, isFetchingContacts, currentPartnerStatus])

    useLayoutEffect(() => {
      if (partnerId) {
        resetSettings()
      }

      return () => {
        resetSettings()
      }
    }, [partnerId, resetSettings])

    useLayoutEffect(() => {
      setExpandedContacts((prev) => {
        if (prev.length) {
          return []
        }
        return prev
      })
    }, [])

    useEffect(() => {
      if (!selectedPartner && !!currentPartner) {
        setSelectedPartner(currentPartner)
      }
    }, [currentPartner, selectedPartner])

    // ----------------------------- RENDER ---------------------------------

    const renderContactHeader = useCallback(
      () => (
        <SimpleTable.HeaderRow>
          <SimpleTable.HeaderCell
            className={cn(styles.userSelectorCell, {
              [styles.hasShadow]: hasShadow,
            })}
          >
            <div className={styles.userSelector}>
              <Checkbox
                checked={isSelectedAllContacts}
                onChange={handleOnMassContactSelect}
                indeterminate={isSelectedSomeContacts}
              />
            </div>
          </SimpleTable.HeaderCell>
          {...contactTableColumns.map((column) => (
            <SimpleTable.HeaderCell key={column.columnField as string} style={column.styles}>
              <CustomTableHeaderCell
                column={column}
                sortOrder={sortOrder[column.columnField]}
                onSortOrderChange={onSortOrderChangeHandler}
              />
            </SimpleTable.HeaderCell>
          ))}
          {onContactSelect && (
            <SimpleTable.HeaderCell
              style={{ width: '62px' }}
              className={styles.contactSelectCell}
            />
          )}
          <SimpleTable.HeaderCell style={{ width: '62px' }} className={styles.userEditCell} />
        </SimpleTable.HeaderRow>
      ),
      [
        handleOnMassContactSelect,
        hasShadow,
        isSelectedAllContacts,
        isSelectedSomeContacts,
        onContactSelect,
        onSortOrderChangeHandler,
        sortOrder,
      ]
    )

    const renderContactRow = useCallback(
      (contact: IContact, index: number) => (
        <SimpleTable.BodyRow
          key={getContactKey(contact)}
          className={cn({
            [styles.inactive]: contact.status === ContactStatus.Inactive,
            [styles.selectedRow]: selectedContacts.includes(contact),
            [styles.filled]: index % 2,
          })}
          onClick={() => handleOnRowClick(contact)}
        >
          <SimpleTable.BodyCell
            className={cn(styles.userSelectorCell, {
              [styles.hasShadow]: hasShadow,
            })}
          >
            <div className={styles.userSelector}>
              <Checkbox
                checked={selectedContacts.includes(contact)}
                className={styles.userCheckbox}
                onChange={() => handleOnContactSelect(contact)}
              />
              <div className={styles.userCircle}>
                {contact.firstName.slice(0, 1) + contact.lastName.slice(0, 1)}
              </div>
            </div>
            <RenderWhen condition={Boolean(contact.entryId375)}>
              <div className={styles.request} />
            </RenderWhen>
            <RenderWhen condition={Boolean(contact.status === ContactStatus.Inactive)}>
              <div className={styles.inactive} />
            </RenderWhen>
          </SimpleTable.BodyCell>
          {...contactTableColumns.map((column) => (
            <SimpleTable.BodyCell key={column.columnField} style={column.styles}>
              {column.bodyCellRender ? column.bodyCellRender(contact, column.columnField) : '-'}
            </SimpleTable.BodyCell>
          ))}
          {onContactSelect && (
            <SimpleTable.BodyCell className={styles.contactSelectCell}>
              <Button
                color="white"
                iconButton="kbase kbase-add"
                className={styles.actionButton}
                onClick={(event) => onContactSelectHandler(contact, event)}
              />
            </SimpleTable.BodyCell>
          )}
          <SimpleTable.BodyCell className={styles.userEditCell}>
            <Button
              color="white"
              iconButton={isReadOnlyRoleAccess ? 'kbase kbase-eye' : 'kbase kbase-edit'}
              className={styles.actionButton}
              onClick={(event) => onContactEditHandler(contact, event)}
            />
          </SimpleTable.BodyCell>
        </SimpleTable.BodyRow>
      ),
      [
        getContactKey,
        selectedContacts,
        hasShadow,
        isReadOnlyRoleAccess,
        handleOnRowClick,
        handleOnContactSelect,
        onContactEditHandler,
        onContactSelect,
        onContactSelectHandler,
      ]
    )

    const renderExpandedRow = useCallback(
      (contact: IContact) => {
        const contactFlags = getContactFlags(contact)
        return (
          <SimpleTable.BodyRow
            className={styles.expandedRow}
            key={`${getContactKey(contact)}-expanded`}
            data-expanded
          >
            <SimpleTable.BodyCell className={styles.expandedCell} colSpan={tableColumnsCount}>
              <div className={styles.expandedContent}>
                <ContactDetails contact={contact} contactFlags={contactFlags} />
              </div>
            </SimpleTable.BodyCell>
          </SimpleTable.BodyRow>
        )
      },
      [getContactFlags, getContactKey, tableColumnsCount]
    )

    const renderContactBody = useCallback(
      () =>
        paginatedList.map((contact, index) => {
          const contactRow = renderContactRow(contact, index)

          if (expandedContacts.includes(contact)) {
            return [contactRow, renderExpandedRow(contact)]
          }

          return contactRow
        }),
      [expandedContacts, paginatedList, renderContactRow, renderExpandedRow]
    )

    return (
      <div className={styles.container} style={{ minHeight: '862px' }}>
        <LoaderOverlay isVisible={isLoading} color="white" />
        {resolvedConfig.isPartnerSelectorVisible && (
          <PartnerSelector partnerId={partnerId} onPartnerSelect={handleOnSelectPartner} />
        )}
        <div className={styles.contacts}>
          <div className={styles.header}>
            <ContactTypeSelector
              items={resolvedConfig.segments}
              onSegmentSelect={handleOnSegmentSelect}
              selectedSegment={selectedFilter}
              contactsBySegment={contactsBySegment}
            />
            <RenderWhen condition={hasMissedRoles}>
              <div className={styles.warning}>
                <InfoBlock text={missedRolesInfo} type={InfoBlockType.Danger} />
              </div>
            </RenderWhen>
            <div style={{ display: 'flex', gap: '8px' }}>
              <ContactControls
                numberOfExpandedContacts={expandedContacts.length}
                numberOfFilteredContacts={filteredContacts.length}
                numberOfSelectedContacts={selectedContacts.length}
                onCollapseContacts={onContactsCollapseHandler}
                onCreateContact={createContactHandler}
                onEditContact={onSelectedContactsEditHandler}
                onExportContacts={onContactsExportHandler}
                onExportSelectedContacts={onContactsExportHandler}
                onSearchQueryChange={setSearchQuery}
                isReadOnly={isReadOnlyRoleAccess}
              />
              <FilterPanel
                numberOfFilteredContacts={filteredContacts.length}
                onSearchQueryChange={setSearchQuery}
                onFilterChange={setIndividualFilters}
              />
            </div>
          </div>
          <div className={styles.tableWrapper} onScroll={handleScroll} ref={tableRef}>
            <SimpleTable
              className={cn(styles.table, styles.partnerContactsTable)}
              placeholderColspan={tableColumnsCount}
            >
              {renderContactHeader()}
              {renderContactBody()}
            </SimpleTable>
          </div>
          <div className={styles.tableFooter}>
            <SimpleTable.Pagination
              page={page}
              pageSize={pageSize}
              total={total}
              classes={{ container: styles.pagination, button: styles.paginationButton }}
              onPageChange={onPageChange}
            />
          </div>
        </div>
        <RenderWhen condition={isContactEditorVisible}>
          <ContactEditor
            contact={currentEditableContactWithOnboardingRequestId!}
            contactFlags={currentEditableContactFlags}
            isVisible={Boolean(currentEditableContact)}
            source={source}
            onReplacementContactCreate={onReplacementContactCreateHandler}
            onClose={onContactEditorCloseHandler}
            onContactFlagsUpdate={onContactFlagsUpdateHandler}
            onContactUpdate={onContactUpdateHandler}
            isReadOnly={isReadOnlyRoleAccess}
            isNewContact={false}
            isOnboarding={resolvedConfig.isOnboarding}
          />
        </RenderWhen>
        <ContactCreator
          onClose={onContactCreatorCloseHandler}
          onCreate={onContactCreatorApplyHandler}
          partner={selectedPartner!}
          isVisible={isNewContactDialogVisible}
          onReactivate={onContactCreatorApplyHandler}
          initialValues={initialValues}
          source={source}
        />
        <DraftContactDialog
          isVisible={isDraftContactDialogVisible}
          partner={selectedPartner!}
          onClose={onDraftContactCreatorCloseHandler}
          onContinueContactCreation={handleOnContinueContactCreation}
          onCreateAsDraft={onCreateAsDraft}
          appSource={source}
        />
      </div>
    )
  },
  (prev, next) =>
    prev.partnerId === next.partnerId &&
    prev.onboardingRequestId === next.onboardingRequestId &&
    prev.source === next.source &&
    prev.config === next.config &&
    prev.onContactSelect === next.onContactSelect
)

export const getMetaInfo = () => {
  return {
    urlMapping: [{ propName: 'partnerId', isRequired: true }],
  }
}

PartnerContacts.displayName = 'PartnerContacts'
